import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MAX_UTTERANCE_SECONDS,
  speechSupported,
  startRecording,
  stopSpeaking,
  warmUpSpeech,
  type Recording,
  type SpeechState,
} from '../lib/speech';

/**
 * The microphone, for asking out loud.
 *
 * Press to start, press again to send — rather than hold-to-talk. A learner
 * dictating a question looks at the box while they speak, and a button that
 * has to stay held is one they will release mid-sentence. The press that
 * stops it is also the press that asks, so there is no separate send.
 *
 * The states are shown, not implied, because the first few seconds are a
 * model loading and silence there reads as a broken button:
 *
 *   idle      — microphone, nothing happening
 *   loading   — the recogniser is being read for the first time this tab
 *   listening — audio is arriving, and the ring moves with the voice
 *   thinking  — recording stopped, Whisper is transcribing
 *
 * Nothing is uploaded at any point; see lib/speech.ts.
 */

interface Props {
  /** Called with what was said. Empty strings are swallowed here. */
  onTranscript: (text: string) => void;
  /** While the assistant is answering, the microphone is out of the way. */
  disabled?: boolean;
  /** Shown as the control's name. */
  label?: string;
}

export function VoiceButton({ onTranscript, disabled, label = 'Ask by voice' }: Props) {
  const [state, setState] = useState<SpeechState>('idle');
  const [level, setLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const recordingRef = useRef<Recording | null>(null);
  const frameRef = useRef(0);
  const timeoutRef = useRef(0);
  // Set when the component goes away mid-recording, so the async stop that is
  // already in flight does not call setState on something unmounted.
  const liveRef = useRef(true);

  // Loaded when the panel opens rather than when the microphone is pressed.
  // The model takes a couple of seconds to read the first time, and that is
  // time the learner would otherwise spend watching "Starting…" before they
  // are allowed to say anything. Opening the panel is a good enough signal of
  // intent, and a visit that never presses the button pays only for a fetch
  // the browser caches.
  useEffect(() => {
    void warmUpSpeech();
  }, []);

  useEffect(() => {
    liveRef.current = true;
    return () => {
      liveRef.current = false;
      cancelAnimationFrame(frameRef.current);
      window.clearTimeout(timeoutRef.current);
      recordingRef.current?.cancel();
      recordingRef.current = null;
    };
  }, []);

  const supported = speechSupported();

  const finish = useCallback(async () => {
    const recording = recordingRef.current;
    if (!recording) return;
    recordingRef.current = null;
    cancelAnimationFrame(frameRef.current);
    window.clearTimeout(timeoutRef.current);

    if (liveRef.current) {
      setState('thinking');
      setLevel(0);
    }

    try {
      const text = await recording.stop();
      if (!liveRef.current) return;
      setState('idle');
      // Silence, or one of Whisper's stock phrases. Nothing was said, so
      // nothing is put in the box — an empty press should look like an empty
      // press, not like a question nobody asked.
      if (text) onTranscript(text);
    } catch {
      if (!liveRef.current) return;
      setState('idle');
      setError('That could not be transcribed. Please try again, or type it.');
    }
  }, [onTranscript]);

  const begin = useCallback(async () => {
    setError(null);
    // A reply being read aloud would otherwise be picked up by the
    // microphone, and the assistant would transcribe itself.
    stopSpeaking();

    setState('loading');
    // Started before the microphone so the two waits overlap; by the time
    // permission is granted the model is usually already in memory.
    void warmUpSpeech();

    let recording: Recording;
    try {
      recording = await startRecording({
        // The sentence ending is what stops the recording, not a second
        // press. `finish` is idempotent — it clears the ref first — so a
        // press that lands at the same moment does no harm.
        onSpeechEnd: () => void finish(),
        onNothingHeard: () => void finish(),
      });
    } catch (caught) {
      if (!liveRef.current) return;
      setState('idle');
      const name = (caught as DOMException)?.name;
      setError(
        name === 'NotAllowedError'
          ? 'The browser is blocking the microphone for this site.'
          : name === 'NotFoundError'
            ? 'No microphone was found.'
            : 'The microphone could not be started.',
      );
      return;
    }

    if (!liveRef.current) {
      recording.cancel();
      return;
    }

    recordingRef.current = recording;
    setState('listening');

    const tick = () => {
      if (!recordingRef.current) return;
      setLevel(recordingRef.current.level());
      frameRef.current = requestAnimationFrame(tick);
    };
    frameRef.current = requestAnimationFrame(tick);

    // A recording nobody stops is a microphone left open. Whisper will not
    // take more than this anyway.
    timeoutRef.current = window.setTimeout(() => void finish(), MAX_UTTERANCE_SECONDS * 1000);
  }, [finish]);

  if (!supported) return null;

  const busy = state === 'loading' || state === 'thinking';
  const listening = state === 'listening';

  return (
    <div className="voice">
      <button
        type="button"
        className={`voice__btn${listening ? ' is-live' : ''}${busy ? ' is-busy' : ''}`}
        onClick={() => (listening ? void finish() : void begin())}
        disabled={disabled || busy}
        aria-label={listening ? 'Stop listening and ask now' : label}
        aria-pressed={listening}
        title={listening ? 'Stop listening and ask now' : label}
      >
        {/* The ring grows with the voice, so a learner can see they are being
            heard without us having to claim it in words. */}
        <span
          className="voice__ring"
          style={{ transform: `scale(${1 + (listening ? level : 0) * 0.55})` }}
          aria-hidden="true"
        />
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          {busy ? (
            <path
              d="M12 3a9 9 0 1 0 9 9"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          ) : (
            <>
              <rect x="9" y="2.5" width="6" height="11" rx="3" fill="currentColor" />
              <path
                d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21M8.5 21h7"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </>
          )}
        </svg>
      </button>

      {/* One line, and only while something is happening. A permanent caption
          under a microphone is clutter. */}
      {(busy || listening || error) && (
        <span className={`voice__hint${error ? ' is-error' : ''}`} role="status">
          {error
            ? error
            : state === 'loading'
              ? 'Starting…'
              : state === 'thinking'
                ? 'Transcribing…'
                : // It stops by itself, so the hint says what to do (nothing)
                  // rather than naming a control. The button is still there
                  // for anyone who wants to cut it short.
                  'Listening — just stop speaking when you are done'}
        </span>
      )}
    </div>
  );
}
